<template>
  <div v-if="loadFailed" class="text-center p-5">
    <p class="text-danger mb-3">{{ loadErrorMessage }}</p>
    <button type="button" class="btn btn-outline-primary" @click="retryLoad">
      Повторить
    </button>
  </div>
  <detailed-project-skeleton v-else-if="!contentReady" />
  <template v-else>
  <div class="nameContainer">
    <img :src="ArrowIcon" alt="Back" class="icon" @click="$router.back()"/>
    <div class="name">{{ project.general?.name }}</div>
  </div>
  <div class="textOffer">
    Оферта ID {{ project.paymentCode }}
  </div>
  <div v-if="project.uuid" class="detailed-project">
    <DetailedProjectOverview
        :project="project"
        :projectStatus="projectStatus"
    />
    <div class="investment-wrapper">
      <DetailedProjectInvestment
          :profile="profile"
          :project="project"
      />
      <div v-if="project.settings.isCollateralRegistration" class="overlay-full">
        <div class="overlay-text">Идёт регистрация залога</div>
      </div>
    </div>
  </div>
  <div class="warnings">
    <div v-if="!project.settings?.smsEnabled" class="card dpi_border_success">
      <div class="card-body">
        Для инвестирования необходимо подписать документы с помощью Усиленной Цифровой Подписи на токене для
        Росреестра.
        Для получения подробностей просим написать на почту <a href="mailto:support@lendly.ru">support@lendly.ru</a>
      </div>
    </div>
    <div v-if="project.settings?.offerAssigmentEnabled" class="card dpi_border_success">
      <div class="card-body">
        Обязательный выкуп платформой займа на 63-й день просроченной задолженности по оплате процентов; на 33-й день просроченной задолженности по возврату суммы займа
      </div>
    </div>
    <div v-if="project.settings?.smsEnabled" class="card dpi_border_success">
      <div class="card-body">
        Для инвестирования необходимо подписать документы с помощью простой подписи (смс-кода) или Усиленной Цифровой
        Подписи на токене для Росреестра
      </div>
    </div>
    <div v-if="investmentNotAllowed" class="card dpi_border_success">
      <div class="card-body">
        В Вашем аккаунте отключена возможность инвестирования.<br>
        Для более детальной информации обратитесь в службу технической поддержки:
        <a href="mailto:support@lendly.ru" class="font-weight-bold">support@lendly.ru</a>
      </div>
    </div>
    <div v-if="requiresNotary"
         class="card dpi_border_success">
      <div class="card-body">
        <p>
          Для инвестирования на первичном рынке необходимо предоставить нотариальную доверенность с
          тождественным заверением подписи нотариуса, выданную на имя ООО Лендли, как управляющего залогом, на
          заключение договора ипотеки в качестве залогодержателя, его государственную регистрацию и
          последующее управление залогом.
        </p>
        <p>
          Скан доверенности в формате pdf и электронный формат тождественности нотариальной подписи в формате
          sig просим отправить на почту
          <a href="mailto:support@lendly.ru">support@lendly.ru</a>
        </p>
        <a href="https://cdn.lendly.ru/documents/doverenost_template.pdf" download
           class="btn btn-success mt-2">
          Шаблон доверенности
        </a>
      </div>
    </div>
    <div v-if="rulesNotConfirmed" class="card border-error">
      <div class="card-body rules-card">
        <img class="warning-img" src="@/assets/img/attention.png" alt="!">
        <div>
          Пожалуйста
          <router-link :to="{ name: 'rules-confirmation' }" class="text">ознакомьтесь с новыми
            правилами
            платформы
          </router-link>
        </div>
      </div>
    </div>
    <div v-if="(this.project.loan?.lenderReservedSum?.amount ?? 0) !== 0" class="card dpi_border_success">
      <div v-if="(this.project.loan?.lenderReservedSum?.amount ?? 0) !== 0" class="card-body">
        Зарезервированная сумма:
        <money :money="this.project.loan?.lenderReservedSum"/>
      </div>
      <div class="card-body">
        <b>Обратите внимание:</b> для успешной инвестиции в проект, необходимо осуществить единовременный
        платеж на всю зарезервированную сумму.
        В противном случае, ваша резервация будет аннулирована.
        Пожалуйста, удостоверьтесь, что сумма вашей инвестиции соответствует зарезервированной, чтобы избежать
        утраты резервации.
      </div>
    </div>
  </div>
  <DetailedProjectTabs
      :project="project"
      :isFundRaising="isFundRaising"
      :secondaryMarket="secondaryMarket"
      :siteUrlHost="siteUrlHost"
      :buy="buy"
  />
  <processing :show="processing"/>
  </template>
</template>

<script>
import {ProjectStatus, Unauthorized, ValidationError} from "@/utils/api-client";
import {OutdatedPassword} from "@/utils/api-client/errors/OutdatedPassword";
import {AuthTokenExpired} from "@/utils/errors";
import {createProjectProvider} from "@/utils/di";
import moment from "moment";
import CTooltip from "./CTooltip";
import DateFormat from "./DateFormat";
import DetailedProjectSkeleton from "./DetailedProjectSkeleton.vue";
import InputErrors from "./InputErrors";
import InputMoney from "./InputMoney";
import Money from "./Money";
import Processing from "./Processing";
import ProjectOffers from "./ProjectOffers";
import InvestProgressBar from "@/components/common/components/InvestProgressBar.vue";
import DetailedProjectTabs from "@/components/common/components/DetailedProjectTabs.vue";
import DetailedProjectOverview from "@/components/common/components/DetailedProjectOverview.vue";
import DetailedProjectInvestment from "@/components/common/components/DetailedProjectInvestment.vue";
import {RouterLink} from "vue-router";
import PublicLink from "@/components/common/components/PublicLink.vue";
import ArrowIcon from '@/assets/img/arrow-left.svg';

export default {
  name: "DetailedProject",
  components: {
    DetailedProjectInvestment,
    DetailedProjectOverview,
    DetailedProjectTabs,
    DetailedProjectSkeleton,
    InvestProgressBar,
    PublicLink,
    CTooltip,
    DateFormat,
    InputErrors,
    InputMoney,
    Money,
    Processing,
    ProjectOffers,
    RouterLink
  },
  inject: ["profile"],
  props: {
    uuid: {
      type: String,
      required: true
    },
    buy: {
      type: Boolean,
      default: true
    },
    card: {
      type: Boolean,
      default: true
    },
    secondaryMarket: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      ArrowIcon,
      project: {},
      investmentAmount: null,
      errors: {},
      processing: false,
      loadFailed: false,
      loadErrorMessage: "",
      contentReady: false
    };
  },
    async mounted() {
    await this.loadProject();
  },
  watch: {
    uuid() {
      this.loadProject();
    }
  },
  computed: {
    isFundRaising() {
      return this.project.status === ProjectStatus.FUND_RAISING;
    },
    projectStatus() {
      if (this.project.status) {
        if (this.project.status === ProjectStatus.REVIEW || this.project.status === ProjectStatus.FINAL_REVIEW) {
          return "На рассмотрении";
        } else if (this.project.status === ProjectStatus.REVIEW_APPROVED) {
          return "Заполнение информации";
        } else if (this.project.status === ProjectStatus.REVIEW_REJECTED || this.project.status === ProjectStatus.FINAL_REVIEW_REJECTED) {
          return "Отклонено";
        } else if (this.project.status === ProjectStatus.FINAL_REVIEW_APPROVED) {
          return "Публикация проекта";
        } else if (this.project.status === ProjectStatus.FUND_RAISING) {
          let diff = moment(this.project.loan?.maturityDate).diff(new Date());
          if (isNaN(diff) || diff < 0) {
            diff = 0;
          }
          const daysLeft = Math.ceil(diff / (60 * 60 * 24 * 1000));
          const reminder = daysLeft % 10;
          const leftCaptions = {1: "Остался"};
          const leftCaption = leftCaptions[reminder] || "Осталось";
          const dayCaptions = {1: "день", 2: "дня", 3: "дня", 4: "дня"};
          const dayCaption = dayCaptions[reminder] || "дней";
          return `${leftCaption} ${daysLeft} ${dayCaption}`;
        } else if (this.project.status === ProjectStatus.FINISHED || this.project.status === ProjectStatus.LOAN_PAYMENT) {
          return "Проинвестирован";
        } else {
          return "Завершен";
        }
      }
    },
    siteUrlHost() {
      const uri = this.project.general?.siteUri;
      if (!uri) {
        return "";
      }
      try {
        return new URL(uri).hostname;
      } catch (err) {
        console.error("siteUrlHost: invalid siteUri", uri, err);
        return "";
      }
    },
    primaryMarketAllow() {
      return this.profile.primaryMarketAllow;
    },
    investmentNotAllowed() {
      return this.profile.lenderVerified && !this.profile.investmentAllow;
    },
    rulesNotConfirmed() {
      return this.profile.lenderVerified && !this.profile.isRulesConfirmed;
    },
    requiresNotary() {
      return this.profile.lenderVerified && !this.profile.primaryMarketAllow && !this.project.loan?.noProxy;
    },
  },
  methods: {
    redirectIfAuthError(err) {
      if (err instanceof Unauthorized || err instanceof AuthTokenExpired) {
        sessionStorage.setItem("redirectTo", this.$route.path);
        this.$router.replace({ name: "login" });
        return true;
      }
      if (err instanceof OutdatedPassword) {
        this.$router.replace({ name: "outdated-password" });
        return true;
      }
      return false;
    },
    notifyLoadError(contextLabel, err, fallbackMessage) {
      console.error(`[DetailedProject] ${contextLabel}`, err);
      if (this.redirectIfAuthError(err)) {
        return;
      }
      if (err instanceof ValidationError) {
        this.$notifications.addAlert(err.message);
        return;
      }
      const msg =
        err &&
        err.response &&
        err.response.data &&
        err.response.data.message;
      const detail = msg || (err && err.message);
      this.$notifications.addAlert(
        detail ? `${contextLabel}: ${detail}` : fallbackMessage
      );
    },
    preloadProjectPhotos(project, timeoutMs = 20000) {
      const urls = (project && project.general && project.general.photoUrls) || [];
      const list = Array.isArray(urls) ? urls.filter(Boolean) : [];
      if (list.length === 0) {
        return Promise.resolve();
      }
      const loadOne = (url) =>
        new Promise((resolve) => {
          const img = new Image();
          const done = () => resolve();
          img.onload = done;
          img.onerror = done;
          img.src = url;
        });
      const all = Promise.all(list.map(loadOne));
      return Promise.race([
        all,
        new Promise((resolve) => setTimeout(resolve, timeoutMs))
      ]);
    },
    async loadProject() {
      this.loadFailed = false;
      this.loadErrorMessage = "";
      this.contentReady = false;
      this.project = {};
      try {
        const provider = await createProjectProvider();
        const data = await provider.getOne(this.uuid);
        this.project = data;
        await this.preloadProjectPhotos(data);
        this.contentReady = true;
      } catch (e) {
        this.notifyLoadError(
          "Карточка проекта",
          e,
          "Не удалось загрузить карточку проекта."
        );
        const authRedirect =
          e instanceof Unauthorized ||
          e instanceof AuthTokenExpired ||
          e instanceof OutdatedPassword;
        if (!authRedirect) {
          this.loadFailed = true;
          this.loadErrorMessage =
            e && e.response && e.response.data && e.response.data.message
              ? e.response.data.message
              : "Не удалось загрузить карточку проекта.";
        }
      }
    },
    retryLoad() {
      return this.loadProject();
    }
  }
}
</script>

<style scoped lang="scss">
.nameContainer {
  display: flex;
  flex-direction: row;
  margin: 1rem;
  gap: 1rem;
}
.name {
  color: var(--dark-bgs);
  font-size: 2.25rem;
}
.detailed-project {
  display: flex;
  flex-direction: row;
  font-weight: 500;
  gap: 0.5rem;
  margin-bottom: 1rem;
  @media (max-width: 768px) {
    flex-direction: column;
  }
}

.textOffer {
  text-transform: uppercase;
  margin: 1rem;
  font-size: 0.8rem;
  color: var(--secondary-gray);
}
.investment-wrapper {
  position: relative;
  width: 40%;
  @media (max-width: 768px) {
    width: 100%;
  }
}

.warnings {
  display: flex;
  background: var(--white);
  flex-direction: column;
  padding: 1.5rem;
  border-radius: 0.8rem;
  margin-bottom: 1rem;
}
.rules-card {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}
.overlay-full {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(255,255,255,0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 0.8rem;

  .overlay-text {
    background: rgba(255,255,255,0.8);
    padding: 1rem 2rem;
    border-radius: 0.5rem;
    font-size: 1.5rem;
    color: var(--secondary-gray);
    font-weight: 400;
  }
}
.dpi_border_success {
  border-color: var(--primary-green-color);
}
</style>